// The changes grid (spec/dashboard.md section 12.1, 12.5): one section per kind, a row per path with its status, the
// entity's name linking to its editor, and the field diffs — one line per changed leaf, the first three shown and the
// rest behind "N more". Used by the commit detail, Compare, the revert / cherry-pick previews and a merge request's Changes.
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useOptionalContent } from '../content/ContentContext';
import { useGame } from '../shell/GameContext';
import { Badge, type Tone } from '../ui/Chip';
import { formatValue } from '../ui/DiffView';
import { STATUS_WORD, changeHref, changeTitle, groupChanges, hasContent, leafDiffs, type ContentChange } from './changes';
import './history.css';
import { Diff } from '../ui/icons';

const STATUS_TONE: Record<string, Tone> = { added: 'success', modified: 'warning', deleted: 'danger', renamed: 'info' };
const SHOWN_LEAVES = 3;

export interface ChangesGridProps {
  changes: ContentChange[];
  branch: string;
  /** the answer's `contentTruncated`: content was attached to the first 500 changes only */
  contentTruncated?: boolean;
  emptyMessage?: string;
}

export function ChangesGrid({ changes, branch, contentTruncated, emptyMessage = 'No change.' }: ChangesGridProps) {
  const { org, game } = useGame();
  const catalog = useOptionalContent();
  const groups = useMemo(() => groupChanges(changes), [changes]);
  const ctx = useMemo(() => ({ org, game: game.slug, branch, resolve: catalog?.resolve }), [org, game.slug, branch, catalog]);
  if (changes.length === 0)
    return (
      <div className="empty changes-empty" data-testid="changes-empty">
        <span className="empty-icon" aria-hidden="true">
          <Diff />
        </span>
        <p>{emptyMessage}</p>
      </div>
    );
  return (
    <div className="changes-grid" data-testid="changes-grid">
      <p className="muted small changes-summary">
        {changes.length} change{changes.length === 1 ? '' : 's'} in {groups.length} group{groups.length === 1 ? '' : 's'}
        {contentTruncated ? ' · content shown for the first 500 changes' : ''}
      </p>
      {groups.map((g) => (
        <section key={g.key} className="changes-group" data-testid={`changes-group-${g.key}`} aria-label={g.title}>
          <h3 className="changes-group-title">
            {g.title} <span className="muted small">({g.changes.length})</span>
          </h3>
          {g.changes.map((c) => {
            const href = changeHref(c, ctx);
            const title = changeTitle(c, catalog?.label, catalog?.resolve);
            return (
              <div key={c.path} className="change-row" data-testid={`change-${c.path}`}>
                <div className="row change-row-head">
                  <Badge tone={STATUS_TONE[c.status] ?? 'neutral'}>{STATUS_WORD[c.status] ?? c.status}</Badge>
                  {href ? (
                    <Link to={href} className="change-title">
                      {title}
                    </Link>
                  ) : (
                    <span className="change-title">{title}</span>
                  )}
                  {c.id && (
                    <code className="small muted" title={c.id}>
                      {c.id.slice(0, 8)}
                    </code>
                  )}
                  <span className="spacer" />
                  <code className="small muted change-path" title={c.oldPath ? `${c.oldPath} → ${c.path}` : c.path}>
                    {c.oldPath ? `${c.oldPath} → ` : ''}
                    {c.path}
                  </code>
                </div>
                <LeafLines change={c} />
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

function LeafLines({ change }: { change: ContentChange }) {
  const [all, setAll] = useState(false);
  const rows = useMemo(() => leafDiffs(change), [change]);
  if (!hasContent(change)) return <div className="muted small change-no-content">content not included</div>;
  if (!rows || rows.length === 0) return <div className="muted small change-no-content">{change.status === 'renamed' ? 'renamed, the content is the same' : 'no field changed'}</div>;
  const shown = all ? rows : rows.slice(0, SHOWN_LEAVES);
  return (
    <div className="leaf-lines" data-testid="leaf-lines">
      {shown.map((r) => (
        <div key={r.path} className="leaf-line">
          <span className="path">{r.path}</span>
          <span className="values">
            {change.status !== 'added' && <span className="before">{formatValue(r.before)}</span>}
            {change.status !== 'added' && change.status !== 'deleted' && <span className="arrow"> → </span>}
            {change.status !== 'deleted' && <span className="after">{formatValue(r.after)}</span>}
          </span>
        </div>
      ))}
      {rows.length > SHOWN_LEAVES && (
        <button type="button" className="linklike small" onClick={() => setAll((v) => !v)}>
          {all ? 'Show less' : `${rows.length - SHOWN_LEAVES} more`}
        </button>
      )}
    </div>
  );
}
